import DownloadIcon from '@mui/icons-material/Download'
import SearchIcon from '@mui/icons-material/Search'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import type { SelectChangeEvent } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { sendGet, sendPost } from 'hooks/use-fetch'

type AttendanceReportView = 'AttendanceByStudent' | 'AttendanceByDay'
type AttendanceThresholdFilter = 'All' | 'Under50' | 'Under30'

interface AttendanceReportOption {
  id: number
  name: string
}

interface AttendanceReportOptions {
  from: string
  to: string
  providers: AttendanceReportOption[]
  sessions: AttendanceReportOption[]
  serviceTypes: AttendanceReportOption[]
  programTypes: AttendanceReportOption[]
}

interface AttendanceReportQuery {
  from: string
  to: string
  providerId?: number
  sessionId?: number
  serviceTypeId?: number
  programTypeId?: number
  reportView: AttendanceReportView
  attendanceThreshold: AttendanceThresholdFilter
}

interface AttendanceReport {
  metadata: AttendanceReportMetadata
  dateColumns: AttendanceReportDateColumn[]
  studentRows: AttendanceStudentReportRow[]
  dayRows: AttendanceDayReportRow[]
  summary: AttendanceReportSummary
}

interface AttendanceReportMetadata {
  generatedAt: string
  isd: string
  provider: string
  facility: string
  session: string
  programType: string
  serviceType: string
  from: string
  to: string
  generatedBy: string
  attendanceThresholdFilter: string
  reportView: string
  legend: string
}

interface AttendanceReportDateColumn {
  attendanceDate: string
  label: string
}

interface AttendanceStudentReportRow {
  studentId: number
  studentName: string
  provider: string
  facility: string
  session: string
  programType: string
  serviceType: string
  dateCells: AttendanceReportDateCell[]
  daysPresent: number
  daysAbsent: number
  daysTardy: number
  possibleDays: number
  attendanceRate?: number | null
  tardyRate?: number | null
  tardyFlag: boolean
  lastEdited?: string | null
  lastEditedBy?: string | null
}

interface AttendanceReportDateCell {
  attendanceDate: string
  value: string
}

interface AttendanceDayReportRow {
  attendanceDate: string
  provider: string
  facility: string
  session: string
  programType: string
  serviceType: string
  totalStudents: number
  possibleStudentDays: number
  presentCount: number
  absentCount: number
  tardyCount: number
  attendanceRate?: number | null
  tardyRate?: number | null
  tardyFlaggedStudents: number
  isNotInSession: boolean
  notInSessionReason?: string | null
}

interface AttendanceReportSummary {
  totalStudents: number
  totalPossibleStudentDays: number
  totalPresent: number
  totalAbsent: number
  totalTardy: number
  overallAttendanceRate?: number | null
  overallTardyRate?: number | null
  studentsBelow40Percent: number
  students40To79Percent: number
  students80PercentAndAbove: number
  studentsFlaggedForTardiness: number
}

const allValue = ''
const maxReportDays = 366

export function AttendanceDetailReport (): JSX.Element {
  const [options, setOptions] = useState<AttendanceReportOptions>()
  const [query, setQuery] = useState<AttendanceReportQuery>({
    from: dateInputValue(new Date()),
    to: dateInputValue(new Date()),
    reportView: 'AttendanceByStudent',
    attendanceThreshold: 'All'
  })
  const [report, setReport] = useState<AttendanceReport>()
  const [loadingOptions, setLoadingOptions] = useState(true)
  const [loadingReport, setLoadingReport] = useState(false)
  const [error, setError] = useState<string[]>([])
  const reportRequestId = useRef(0)

  useEffect(() => {
    const loadOptions = async (): Promise<void> => {
      const { response, success, error } = await sendGet('/Attendance/Reports/Options', {})
      if (!success) {
        setError(error.filter(message => message.trim() !== '').length === 0 ? ['Attendance report options could not be loaded.'] : error)
        setLoadingOptions(false)
        return
      }

      const nextOptions = response as AttendanceReportOptions
      setOptions(nextOptions)
      setQuery(current => ({
        ...current,
        from: dateInputValue(nextOptions.from),
        to: dateInputValue(nextOptions.to)
      }))
      setLoadingOptions(false)
    }

    void loadOptions()
  }, [])

  const filteredSessions = useMemo(() => {
    if (options === undefined) return []
    if (query.sessionId !== undefined) return options.sessions.filter(session => session.id === query.sessionId)
    return options.sessions
  }, [options, query.sessionId])

  const updateQuery = (updater: (current: AttendanceReportQuery) => AttendanceReportQuery): void => {
    reportRequestId.current += 1
    setQuery(updater)
    setReport(undefined)
    setLoadingReport(false)
  }

  const queryErrors = useMemo(() => validateQuery(query), [query])
  const canSubmitQuery = queryErrors.length === 0

  const executeReport = async (): Promise<void> => {
    if (!canSubmitQuery) {
      setReport(undefined)
      setError([])
      return
    }

    const requestId = reportRequestId.current + 1
    reportRequestId.current = requestId
    const submittedQuery = cleanQuery(query)
    setLoadingReport(true)
    setError([])
    const { response, success, error } = await sendPost('/Attendance/Reports', submittedQuery)
    if (requestId !== reportRequestId.current) {
      return
    }

    setLoadingReport(false)
    if (!success) {
      setError(error.filter(message => message.trim() !== '').length === 0 ? ['Attendance report could not be loaded.'] : error)
      return
    }

    setReport(response as AttendanceReport)
  }

  const clearFilters = (): void => {
    reportRequestId.current += 1
    setQuery({
      from: options === undefined ? dateInputValue(new Date()) : dateInputValue(options.from),
      to: options === undefined ? dateInputValue(new Date()) : dateInputValue(options.to),
      reportView: 'AttendanceByStudent',
      attendanceThreshold: 'All'
    })
    setReport(undefined)
    setError([])
    setLoadingReport(false)
  }

  const downloadReport = async (format: 'csv' | 'xml'): Promise<void> => {
    if (!canSubmitQuery) return

    const endpoint = format === 'csv' ? '/Attendance/Reports/ExportCsv' : '/Attendance/Reports/ExportXml'
    const fileName = format === 'csv' ? 'AttendanceDetailReport.csv' : 'AttendanceDetailReport.xml'
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL ?? ''}${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: format === 'csv' ? 'text/csv' : 'application/xml',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cleanQuery(query))
      })

      if (!response.ok) {
        toast.error(`Export .${format} failed.`)
        return
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      toast.error(`Export .${format} failed.`)
    }
  }

  const displayedErrors = [...error, ...queryErrors]

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Attendance Detail Report</Typography>

      {displayedErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>{displayedErrors.join(' ')}</Alert>
      )}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2.5, p: 2 }}>
        {loadingOptions && (
          <Box role="status" sx={{ alignItems: 'center', display: 'flex', gap: 1.5, py: 1 }}>
            <CircularProgress size={22} />
            <Typography>Loading report filters.</Typography>
          </Box>
        )}

        {!loadingOptions && options !== undefined && (
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' } }}>
            <ReportSelect
              label="Provider"
              value={query.providerId}
              options={options.providers}
              onChange={providerId => { updateQuery(current => ({ ...current, providerId })) }}
            />
            <ReportSelect
              label="Session"
              value={query.sessionId}
              options={filteredSessions}
              onChange={sessionId => { updateQuery(current => ({ ...current, sessionId })) }}
            />
            <ReportSelect
              label="Service Type"
              value={query.serviceTypeId}
              options={options.serviceTypes}
              onChange={serviceTypeId => { updateQuery(current => ({ ...current, serviceTypeId })) }}
            />
            <ReportSelect
              label="Program Type"
              value={query.programTypeId}
              options={options.programTypes}
              onChange={programTypeId => { updateQuery(current => ({ ...current, programTypeId })) }}
            />
            <TextField
              label="Date Range"
              type="date"
              value={query.from}
              onChange={event => { updateQuery(current => ({ ...current, from: event.target.value })) }}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="End Date"
              type="date"
              value={query.to}
              onChange={event => { updateQuery(current => ({ ...current, to: event.target.value })) }}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <FormControl size="small">
              <InputLabel id="attendance-report-view-label">Report View</InputLabel>
              <Select
                labelId="attendance-report-view-label"
                label="Report View"
                value={query.reportView}
                onChange={(event: SelectChangeEvent) => { updateQuery(current => ({ ...current, reportView: event.target.value as AttendanceReportView })) }}
              >
                <MenuItem value="AttendanceByStudent">Attendance By Student</MenuItem>
                <MenuItem value="AttendanceByDay">Attendance By Day (High Level)</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel id="attendance-threshold-label">Attendance Threshold</InputLabel>
              <Select
                labelId="attendance-threshold-label"
                label="Attendance Threshold"
                value={query.attendanceThreshold}
                onChange={(event: SelectChangeEvent) => { updateQuery(current => ({ ...current, attendanceThreshold: event.target.value as AttendanceThresholdFilter })) }}
              >
                <MenuItem value="All">All Attendance</MenuItem>
                <MenuItem value="Under50">&lt; 50% Attendance</MenuItem>
                <MenuItem value="Under30">&lt; 30% Attendance</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, gridColumn: { xs: '1', md: '1 / -1' } }}>
              <Button variant="outlined" startIcon={<DownloadIcon />} disabled={!canSubmitQuery} onClick={() => { void downloadReport('csv') }}>
                Export .csv
              </Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} disabled={!canSubmitQuery} onClick={() => { void downloadReport('xml') }}>
                Export .xml
              </Button>
              <Button variant="contained" startIcon={<SearchIcon />} disabled={loadingReport || !canSubmitQuery} onClick={() => { void executeReport() }}>
                View Report Data
              </Button>
              <Button variant="text" startIcon={<RestartAltIcon />} onClick={clearFilters}>
                Clear Filters
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {loadingReport && (
        <Box role="status" sx={{ alignItems: 'center', display: 'flex', gap: 1.5, py: 2 }}>
          <CircularProgress size={22} />
          <Typography>Loading report data.</Typography>
        </Box>
      )}

      {!loadingReport && report !== undefined && <AttendanceReportResults report={report} reportView={query.reportView} />}
    </Box>
  )
}

function ReportSelect (props: {
  label: string
  value?: number
  options: AttendanceReportOption[]
  onChange: (value?: number) => void
}): JSX.Element {
  const labelId = `${props.label.toLowerCase().replace(/\s+/g, '-')}-label`
  return (
    <FormControl size="small">
      <InputLabel id={labelId}>{props.label}</InputLabel>
      <Select
        labelId={labelId}
        label={props.label}
        value={props.value === undefined ? allValue : String(props.value)}
        onChange={(event: SelectChangeEvent) => {
          props.onChange(event.target.value === allValue ? undefined : Number(event.target.value))
        }}
      >
        <MenuItem value={allValue}>All</MenuItem>
        {props.options.map(option => (
          <MenuItem key={option.id} value={String(option.id)}>{option.name}</MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

function AttendanceReportResults (props: { report: AttendanceReport, reportView: AttendanceReportView }): JSX.Element {
  const rows = props.reportView === 'AttendanceByDay' ? props.report.dayRows : props.report.studentRows

  return (
    <Box>
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2, p: 2 }}>
        <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' } }}>
          <Metadata label="Report generated" value={formatDateTime(props.report.metadata.generatedAt)} />
          <Metadata label="ISD" value={props.report.metadata.isd} />
          <Metadata label="Provider" value={props.report.metadata.provider} />
          <Metadata label="Facility" value={props.report.metadata.facility} />
          <Metadata label="Session" value={props.report.metadata.session} />
          <Metadata label="Program Type" value={props.report.metadata.programType} />
          <Metadata label="Service Type" value={props.report.metadata.serviceType} />
          <Metadata label="Date Range" value={`${dateOnly(props.report.metadata.from)} to ${dateOnly(props.report.metadata.to)}`} />
          <Metadata label="Generated By" value={props.report.metadata.generatedBy} />
          <Metadata label="Attendance Threshold Filter" value={props.report.metadata.attendanceThresholdFilter} />
        </Box>
        <Typography sx={{ color: 'text.secondary', mt: 1.5 }}>{props.report.metadata.legend}</Typography>
      </Paper>

      {rows.length === 0 && (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 3, textAlign: 'center' }}>
          <Typography>No report rows match the selected filters.</Typography>
        </Paper>
      )}

      {rows.length > 0 && props.reportView === 'AttendanceByStudent' && <StudentReportTable report={props.report} />}
      {rows.length > 0 && props.reportView === 'AttendanceByDay' && <DayReportTable report={props.report} />}
    </Box>
  )
}

function StudentReportTable (props: { report: AttendanceReport }): JSX.Element {
  return (
    <>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['Student ID', 'Student Name', 'Provider', 'Facility', 'Session', 'Program Type', 'Service Type'].map(label => (
                <TableCell key={label}>{label}</TableCell>
              ))}
              {['Days Present', 'Days Absent', 'Days Tardy', 'Possible Days', 'Attendance Rate', 'Tardy Rate', 'Tardy Flag', 'Last Edited', 'Last Edited By'].map(label => (
                <TableCell key={label} align={label.includes('Rate') || label.includes('Days') ? 'right' : 'left'}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.report.studentRows.map(row => {
              const rowKey = studentRowKey(row)
              return (
                <TableRow key={rowKey}>
                  <TableCell>{row.studentId}</TableCell>
                  <TableCell>{row.studentName}</TableCell>
                  <TableCell>{row.provider}</TableCell>
                  <TableCell>{row.facility}</TableCell>
                  <TableCell>{row.session}</TableCell>
                  <TableCell>{row.programType}</TableCell>
                  <TableCell>{row.serviceType}</TableCell>
                  <TableCell align="right">{row.daysPresent}</TableCell>
                  <TableCell align="right">{row.daysAbsent}</TableCell>
                  <TableCell align="right">{row.daysTardy}</TableCell>
                  <TableCell align="right">{row.possibleDays}</TableCell>
                  <TableCell align="right">{formatRate(row.attendanceRate)}</TableCell>
                  <TableCell align="right">{formatRate(row.tardyRate)}</TableCell>
                  <TableCell>{row.tardyFlag ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{row.lastEdited == null ? '' : formatDateTime(row.lastEdited)}</TableCell>
                  <TableCell>{row.lastEditedBy ?? ''}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <ReportSummary summary={props.report.summary} />
    </>
  )
}

function DayReportTable (props: { report: AttendanceReport }): JSX.Element {
  return (
    <>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Date', 'Provider', 'Facility', 'Session', 'Program Type', 'Service Type', 'Total Students', 'Possible Student-Days', 'Present', 'Absent', 'Tardy', 'Attendance Rate', 'Tardy Rate', 'Tardy Flagged Students', 'Not In Session'].map(label => (
                <TableCell key={label}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.report.dayRows.map(row => (
              <TableRow key={dayRowKey(row)}>
                <TableCell>{dateOnly(row.attendanceDate)}</TableCell>
                <TableCell>{row.provider}</TableCell>
                <TableCell>{row.facility}</TableCell>
                <TableCell>{row.session}</TableCell>
                <TableCell>{row.programType}</TableCell>
                <TableCell>{row.serviceType}</TableCell>
                <TableCell align="right">{row.totalStudents}</TableCell>
                <TableCell align="right">{row.possibleStudentDays}</TableCell>
                <TableCell align="right">{row.presentCount}</TableCell>
                <TableCell align="right">{row.absentCount}</TableCell>
                <TableCell align="right">{row.tardyCount}</TableCell>
                <TableCell align="right">{formatRate(row.attendanceRate)}</TableCell>
                <TableCell align="right">{formatRate(row.tardyRate)}</TableCell>
                <TableCell align="right">{row.tardyFlaggedStudents}</TableCell>
                <TableCell>{row.isNotInSession ? `Yes${row.notInSessionReason == null ? '' : ` - ${row.notInSessionReason}`}` : 'No'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ReportSummary summary={props.report.summary} />
    </>
  )
}

function ReportSummary (props: { summary: AttendanceReportSummary }): JSX.Element {
  const items = [
    ['Total Students', props.summary.totalStudents],
    ['Total Possible Student-Days after excluded days', props.summary.totalPossibleStudentDays],
    ['Total Present', props.summary.totalPresent],
    ['Total Absent', props.summary.totalAbsent],
    ['Total Tardy', props.summary.totalTardy],
    ['Overall Attendance Rate', formatRate(props.summary.overallAttendanceRate)],
    ['Overall Tardy Rate', formatRate(props.summary.overallTardyRate)],
    ['Students in <40% bucket', props.summary.studentsBelow40Percent],
    ['Students in 40-79% bucket', props.summary.students40To79Percent],
    ['Students in 80%+ bucket', props.summary.students80PercentAndAbove],
    ['Students flagged for tardiness', props.summary.studentsFlaggedForTardiness]
  ]

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mt: 2, p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Session Summary</Typography>
      <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' } }}>
        {items.map(([label, value]) => <Metadata key={label} label={String(label)} value={String(value)} />)}
      </Box>
    </Paper>
  )
}

function Metadata (props: { label: string, value: string }): JSX.Element {
  return (
    <Box>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600 }}>{props.label}</Typography>
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{props.value}</Typography>
    </Box>
  )
}

function cleanQuery (query: AttendanceReportQuery): Partial<AttendanceReportQuery> {
  return Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined && value !== '')) as Partial<AttendanceReportQuery>
}

function validateQuery (query: AttendanceReportQuery): string[] {
  const errors: string[] = []
  const from = dateInputTime(query.from)
  const to = dateInputTime(query.to)

  if (from === undefined) {
    errors.push('Start date is required.')
  }

  if (to === undefined) {
    errors.push('End date is required.')
  }

  if (from !== undefined && to !== undefined) {
    if (from > to) {
      errors.push('Start date must be on or before end date.')
    } else {
      const days = Math.floor((to - from) / 86400000) + 1
      if (days > maxReportDays) {
        errors.push(`Date range cannot exceed ${maxReportDays} days.`)
      }
    }
  }

  return errors
}

function dateInputTime (value: string): number | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (match === null) return undefined

  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

function studentRowKey (row: AttendanceStudentReportRow): string {
  return [row.studentId, row.provider, row.facility, row.session, row.programType, row.serviceType].join('|')
}

function dayRowKey (row: AttendanceDayReportRow): string {
  return [row.attendanceDate, row.provider, row.facility, row.session, row.programType, row.serviceType].join('|')
}

function dateInputValue (value: string | Date): string {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10)
  }

  const date = typeof value === 'string' ? new Date(value) : value
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10)
}

function dateOnly (value: string): string {
  return dateInputValue(value)
}

function formatDateTime (value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString()
}

function formatRate (value?: number | null): string {
  return value == null ? '' : `${(value * 100).toFixed(2)}%`
}
